import React, {useEffect, useState} from "react";
import * as axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

const emptyForm = {date: '', km: '', description: '', nextServiceKm: '', cost: ''};

// Parses a "YYYY-MM-DD..." date string (what the backend sends) down to
// just the date part, for both the <input type="date"> value and display.
function dateOnly(value) {
  return (value || '').slice(0, 10);
}

// car is required (never null) — this modal is only ever opened from a
// specific row in Cars.js. onChanged (optional) fires after any
// create/edit/delete, so Cars.js can refresh the due/soon service flag it
// derives from each car's latest service record.
export default function CarServiceModal({open, car, onClose, onChanged}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (open && car) {
      loadServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, car]);

  const loadServices = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/cars/${car.id}/services`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      setServices(response.data || []);
    } catch (e) {
      setLoadError('Eroare la încărcarea istoricului de service.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setFormOpen(true);
  };

  const openEditForm = (service) => {
    setEditingId(service.id);
    setForm({
      date: dateOnly(service.date),
      km: service.km,
      description: service.description || '',
      nextServiceKm: service.next_service_km || '',
      cost: service.cost || '',
    });
    setError('');
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const updateField = (field) => (event) => setForm({...form, [field]: event.target.value});

  const save = async () => {
    if (!form.date || form.km === '') {
      setError('Data și km sunt obligatorii.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const headers = {Authorization: `Bearer ${token}`};
      const payload = {
        car_id: car.id,
        date: form.date,
        km: parseInt(form.km, 10),
        description: form.description,
        next_service_km: form.nextServiceKm === '' ? 0 : parseInt(form.nextServiceKm, 10),
        cost: form.cost === '' ? 0 : parseFloat(form.cost),
      };
      if (editingId) {
        await axios.default.put(`${baseUrl}v1/admin/car-services/${editingId}`, payload, {headers});
      } else {
        await axios.default.post(`${baseUrl}v1/admin/car-services`, payload, {headers});
      }
      setFormOpen(false);
      await loadServices();
      if (onChanged) {
        onChanged();
      }
    } catch (e) {
      setError('Eroare la salvare.');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (service) => {
    if (!window.confirm(`Ștergi intervenția din ${dateOnly(service.date)}?`)) {
      return;
    }
    setDeletingId(service.id);
    try {
      const token = localStorage.getItem('access_token');
      await axios.default.delete(`${baseUrl}v1/admin/car-services/${service.id}`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      await loadServices();
      if (onChanged) {
        onChanged();
      }
    } catch (e) {
      alert('Eroare la ștergerea intervenției.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!car) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <div className="deliverer-modal">
        <div className="deliverer-modal-topbar">
          <h3 className="deliverer-modal-title">
            Istoric service — {car.make} {car.model} ({car.plate_number})
          </h3>
          <IconButton className="deliverer-modal-close" size="small" onClick={onClose}>
            <CloseIcon/>
          </IconButton>
        </div>

        <div className="deliverer-modal-fields">
          <div className="deliverers-section-header">
            <span/>
            <IconButton color="primary" onClick={openCreateForm} title="Adaugă intervenție">
              <AddIcon/>
            </IconButton>
          </div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"/>
            </div>
          ) : loadError ? (
            <div className="deliverers-empty-state">{loadError}</div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Km</TableCell>
                  <TableCell>Ce s-a schimbat</TableCell>
                  <TableCell>Următorul service (km)</TableCell>
                  <TableCell>Cost (lei)</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>{dateOnly(service.date)}</TableCell>
                    <TableCell>{service.km}</TableCell>
                    <TableCell>{service.description}</TableCell>
                    <TableCell>{service.next_service_km || '—'}</TableCell>
                    <TableCell>{service.cost ? `${service.cost} lei` : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEditForm(service)}>
                        <EditIcon fontSize="small"/>
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => deleteService(service)}
                        disabled={deletingId === service.id}
                      >
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {services.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} style={{textAlign: 'center', color: '#888'}}>
                      Nicio intervenție înregistrată.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="xs">
        <DialogTitle>{editingId ? 'Editează intervenție' : 'Adaugă intervenție'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Data"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{shrink: true}}
            value={form.date}
            onChange={updateField('date')}
          />
          <TextField
            label="Km"
            type="number"
            fullWidth
            margin="normal"
            value={form.km}
            onChange={updateField('km')}
          />
          <TextField
            label="Ce s-a schimbat"
            fullWidth
            multiline
            margin="normal"
            value={form.description}
            onChange={updateField('description')}
          />
          <TextField
            label="Următorul service (km)"
            type="number"
            fullWidth
            margin="normal"
            value={form.nextServiceKm}
            onChange={updateField('nextServiceKm')}
          />
          <TextField
            label="Cost (lei)"
            type="number"
            fullWidth
            margin="normal"
            value={form.cost}
            onChange={updateField('cost')}
          />
          {error && <div className="deliverer-modal-error">{error}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>Anulează</Button>
          <Button onClick={save} disabled={saving} variant="contained" color="primary">
            Salvează
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
